import { useCallback, useRef, useState } from "react";

import { buttonClass } from "@/components/buttonStyles";
// BRK-S145 — the drop TARGET, extracted from `DocumentDropzone`.
//
// Three surfaces now want the same affordance and none of them wants the rest
// of each other: the New Business intake (BRK-S038) uploads a packet to storage
// and analyses it, the bond request (BRK-S120) does the same against a
// different target, and a bond order's `files` field posts each document to the
// BFF and records a reference. What they share is exactly this — a dashed
// region that takes a drop, a Browse button over a hidden input, a prompt and a
// hint — and the shared thing is presentational, so extracting it costs nothing
// and copying it a third time would have been the second smell (CLAUDE.md,
// Coding Standards: "if you copy a block a second time, extract it").
//
// It deliberately owns NO upload state. Which files are queued, uploading,
// attached, rejected or removed is entirely the caller's, because that is where
// the three surfaces genuinely differ — and a drop target that also tracked
// files would be the component nobody could reuse.

export function FileDropTarget({
  prompt,
  hint,
  inputLabel,
  accept,
  multiple = true,
  disabled = false,
  onFiles,
}: {
  prompt: string;
  hint: string;
  /** The file input's accessible name — what a test and a screen reader find.
   *  Distinct per instance, so two drop zones on one page are tellable apart. */
  inputLabel: string;
  /** The `accept` attribute, so the system picker offers what the server takes. */
  accept: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const hand = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onFiles(Array.from(files));
    },
    [onFiles],
  );

  return (
    <div
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(false);
        hand(e.dataTransfer.files);
      }}
      className={`flex flex-col items-center gap-3 rounded-xl border border-dashed px-4 py-8 text-center transition-colors sm:py-12 ${
        dragging
          ? "border-brk-accent bg-brk-accent/5"
          : "border-brk-border bg-brk-panel"
      }`}
    >
      <svg
        aria-hidden="true"
        className="h-9 w-9 text-brk-dim"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M7 18a4 4 0 0 1-.6-7.96 5 5 0 0 1 9.7-1.6A3.5 3.5 0 0 1 18 18" />
        <path d="M12 13v6m0-6-2.5 2.5M12 13l2.5 2.5" />
      </svg>
      <span className="text-sm font-medium text-brk-text">{prompt}</span>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className={buttonClass()}
      >
        Browse files
      </button>
      <span className="text-xs text-brk-dim">{hint}</span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        aria-label={inputLabel}
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          hand(e.target.files);
          // Cleared so choosing the SAME file twice fires a change event the
          // second time — a broker who removed a file and picked it again
          // otherwise gets nothing at all.
          e.target.value = "";
        }}
      />
    </div>
  );
}
